import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule, CUSTOM_ELEMENTS_SCHEMA, Injector } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { SeoModule } from '@wdpr/ra-angular-seo-metadata';
import { PageKeyModule } from '@wdpr/ra-angular-page-key';
import { AnalyticsModule } from '@wdpr/angular-analytics';
import { CookieService } from 'ngx-cookie-service';
import { DeviceDetectionModule } from '@wdpr/ra-angular-device-detection';
import { FEATURE_TOGGLE_CONFIG, FeatureToggleModule } from '@wdpr/ra-angular-feature-toggle';
import { PersonalizationModule, P13N_LOADER, P13N_DECISION_ATTRS_LOADER, P13N_BASE } from '@wdpr/ngx-personalization';
import { UrlSerializer } from '@angular/router';
import { GeolocationService, GeolocationModule } from '@wdpr/ra-angular-geolocation';

// Import LACD locales
import { registerLocaleData } from '@angular/common';
import es_AR from '@angular/common/locales/es-AR';
import es_CL from '@angular/common/locales/es-CL';
import es_CO from '@angular/common/locales/es-CO';
import es_MX from '@angular/common/locales/es-MX';
import es_PE from '@angular/common/locales/es-PE';
import pt_BR from '@angular/common/locales/pt';
// Import other countries locales
import en_CA from '@angular/common/locales/en-CA';
import fr_CA from '@angular/common/locales/fr-CA';
import en_GB from '@angular/common/locales/en-GB';
import en_EU from '@angular/common/locales/en-150';
import es_US from '@angular/common/locales/es-US';
// Import HKDL locales
import zh_HK from '@angular/common/locales/zh-Hant';
import zh_CN from '@angular/common/locales/zh-Hans';
import ja from '@angular/common/locales/ja';
import ko from '@angular/common/locales/ko';
import th from '@angular/common/locales/th';
import id from '@angular/common/locales/id';
import ms from '@angular/common/locales/ms';

import { AnimationService } from '@finder/shared/components/animations/animation-service/animation.service';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AuthenticationService } from '@finder/shared/services/authentication/authentication.service';
import { AuthInterceptor } from '@finder/shared/interceptors/auth-interceptor/auth-interceptor.service';
import { CalendarsService } from '@finder/features/calendars/services/calendar.service';
import { ComponentsModule } from '@finder/shared/components/components.module';
import { CONFIG_CONSTANTS, SEO_OPTIONS } from '@finder/shared/constants/app.constants';
import { ConfigService } from '@finder/core/config.service';
import { CoreModule } from '@finder/core/core.module';
import { CustomUrlSerializer } from '@finder/shared/utils/url/customUrlSerializer';
import { DebugLogService } from '@finder/shared/services/debug-log/debug-log.service';
import { DebugPanelModule } from '@finder/shared/components/debug-panel/debug-panel.module';
import { DineToFinderErrorModalComponent } from '@finder/shared/components/dine-to-finder-error-modal/dine-to-finder-error-modal.component';
import { DineToFinderErrorModalModule } from '@finder/shared/components/dine-to-finder-error-modal/dine-to-finder-error-modal.module';
import { ExplorerServiceConnectorService } from '@finder/shared/services/explorer-svc-connector/explorer-svc-connector.service';
import { DiningCalendarDaysService } from '@finder/shared/services/dining-calendar-days/dining-calendar-days.service';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { FinderApiConnectorService } from '@finder/shared/services/finder-api-connector/finder-api-connector.service';
import { FinderPersonalizationService } from '@finder/shared/services/finder-analytics/finder-personalization.service';
import { FinderThemeService } from '@finder/shared/services/theme-service/finder-theme.service';
import { geoConfig } from '@finder/common/geo-config.constants';
import { HeaderOverrideInterceptor } from '@finder/shared/interceptors/header-overrides-interceptor/header-overrides.interceptor';
import { InterstitialModalComponent } from '@finder/shared/components/interstitial-modal/interstitial-modal.component';
import { InterstitialTriggerModule } from '@finder/shared/directives/interstitial-trigger/interstitial-trigger.module';
import { InterstitialService } from '@finder/shared/services/interstitial-service/interstitial-service.service';
import { LanguageBundleConnectorService } from '@finder/shared/services/language-bundle/language-bundle-connector.service';
import { LAZY_MODULES } from '@finder/shared/services/lazy-loader/tokens';
import { lazyArrayToObj } from '@finder/shared/services/lazy-loader/lazy-bundles';
import { LazyLoaderService } from '@finder/shared/services/lazy-loader/lazy-loader-service';
import { ListServiceConnectorService } from '@finder/shared/services/list-svc-connector/list-svc-connector.service';
import { LoadingService } from '@finder/shared/services/loading-service/loading-service.service';
import { LocaleHelperService } from '@finder/shared/services/locale-service/locale-helper.service';
import { LocaleInterceptor } from '@finder/shared/interceptors/locale/locale-interceptor.service';
import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';
import { MediaServiceConnectorService } from '@finder/shared/services/media-svc-connector/media-svc-connector.service';
import { OneIdFactory } from '@finder/shared/services/one-id/one-id.factory';
import { OneIdService } from '@finder/shared/services/one-id/one-id.service';
import { ReservationServiceConnectorService } from '@finder/shared/services/reservation-svc-connector/reservation-svc-connector.service';
import { RouteStateService } from '@finder/shared/services/route-state-service/route-state-service';
import { SearchServiceConnectorService } from '@finder/shared/services/search-svc-connector/search-svc-connector.service';
import { SiteIdInterceptor } from '@finder/shared/interceptors/site-id/site-id-interceptor.service';
import { TranslationHelperService } from '@finder/shared/interceptors/translation/translation-helper.service';
import { TranslationInterceptor } from '@finder/shared/interceptors/translation/translation-interceptor.service';
import { TravelAgentService } from '@finder/shared/services/travel-agent/travel-agent.service';
import { ViewportDetection } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { WebLinkValidationService } from '@finder/shared/services/web-link-validation/web-link-validation.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { TokenInterceptor } from './shared/interceptors/token/token-interceptor.service';

// Register LACD countries locale metadata. Needed for Currency and Decimal pipes.
registerLocaleData(es_AR, 'es-ar');
registerLocaleData(es_CL, 'es-cl');
registerLocaleData(es_CO, 'es-co');
registerLocaleData(es_MX, 'es-mx');
registerLocaleData(es_PE, 'es-pe');
registerLocaleData(pt_BR, 'pt-br');
// Register other countries
registerLocaleData(en_CA, 'en_CA');
registerLocaleData(fr_CA, 'fr-ca');
registerLocaleData(en_GB, 'en_GB');
registerLocaleData(en_EU, 'en-eu');
registerLocaleData(es_US, 'es-us');
// Register HKDL locales
registerLocaleData(zh_HK, 'zh_HK');
registerLocaleData(zh_CN, 'zh_CN');
registerLocaleData(ja, 'ja');
registerLocaleData(ja, 'ja');
registerLocaleData(ko, 'ko');
registerLocaleData(th, 'th');
registerLocaleData(id, 'id');
registerLocaleData(ms, 'ms');

// eslint-disable-next-line @typescript-eslint/naming-convention
declare let __webpack_public_path__: string;

const APP_PROVIDERS = [
    AnimationService,
    AuthenticationService,
    CalendarsService,
    CookieService,
    DiningCalendarDaysService,
    ExplorerServiceConnectorService,
    FinderAnalyticsService,
    FinderApiConnectorService,
    FinderPersonalizationService,
    FinderThemeService,
    GeolocationService,
    LanguageBundleConnectorService,
    ListServiceConnectorService,
    LoadingService,
    InterstitialService,
    LocaleHelperService,
    LocaleService,
    MediaServiceConnectorService,
    ReservationServiceConnectorService,
    RouteStateService,
    SearchServiceConnectorService,
    TranslationHelperService,
    TravelAgentService,
    ViewportDetection,
    WebLinkValidationService,
    WindowRef,
];
@NgModule({
    declarations: [
        AppComponent,
    ],
    imports: [
        AnalyticsModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        BrowserModule,
        ComponentsModule,
        CoreModule,
        DebugPanelModule,
        DeviceDetectionModule,
        InterstitialTriggerModule,
        DineToFinderErrorModalModule,
        FeatureToggleModule,
        GeolocationModule.configure(geoConfig),
        HttpClientModule,
        PageKeyModule,
        PersonalizationModule,
        SeoModule,
    ],
    providers: [
        {
            provide: UrlSerializer,
            useClass: CustomUrlSerializer
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (config: ConfigService, auth: AuthenticationService) => () =>
                config.load()
                    .then((result: any) => {
                        const baseUrl = config.getValue(CONFIG_CONSTANTS.apiUrlKey);
                        const version = config.getValue(CONFIG_CONSTANTS.defaultVersionKey);
                        const cdnList = config.getValue(CONFIG_CONSTANTS.cdnListKey);
                        const cdnPath = config.getValue(CONFIG_CONSTANTS.cdnPathKey);
                        // Set __webpack_public_path__ so the chunks js bundles
                        // for lazy loading has the correct CDN domain.
                        __webpack_public_path__ =
                            (cdnList && cdnList.length > 0 ? cdnList[0] : '') + cdnPath;
                        // ask for token early when the app initializes.
                        auth.getAuthToken(version, baseUrl).subscribe();

                        return Promise.resolve();
                    })
                    .catch((err) => err),
            deps: [ConfigService, AuthenticationService],
            multi: true,
        },
        {
            provide: 'LOGASAURUS_URL',
            useValue: '/advanced-finder/log',
        },
        APP_PROVIDERS,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LocaleInterceptor,
            multi: true,
        },
        // Per https://myjira.disney.com/browse/GIT-33664, removing SPA override for T.com
        // in prepration for Small World
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TranslationInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SiteIdInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        },
        {
            provide: 'SEO_OPTIONS',
            useValue: SEO_OPTIONS,
        },
        {
            provide: FEATURE_TOGGLE_CONFIG,
            useFactory: (config: ConfigService) => {
                const webApiUrl = `${config.getValue('webApiUrl')}/${config.getValue('webApiDefaultVersion')}`;

                return {
                    appTogglesName: 'appToggles',
                    featureToggleUrl: webApiUrl + '/toggles',
                };
            },
            deps: [ConfigService],
        },
        {
            provide: 'PAGE_KEY_URL_ENDPOINT',
            useValue: '/advanced-finder/page-key/',
        },
        {
            provide: 'PAGE_KEY_TEMPLATE',
            useValue: [
                'siteId', 'path',
            ],
        },
        {
            provide: P13N_LOADER,
            useFactory: (finderAnalyticsService: FinderAnalyticsService) => () =>
                finderAnalyticsService.getGuestModel().toPromise(),
            deps: [FinderAnalyticsService],
        },
        {
            provide: P13N_DECISION_ATTRS_LOADER,
            useFactory: (finderAnalyticsService: FinderAnalyticsService) => () =>
                finderAnalyticsService.getDecisionAttributes(),
            deps: [FinderAnalyticsService],
        },
        {
            provide: P13N_BASE,
            useValue: {
                configuration: {
                    CEA: {
                        decisionAttributesLoaderTimeout: 500,
                    },
                },
            },
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HeaderOverrideInterceptor,
            multi: true
        },
        LazyLoaderService,
        {
            provide: LAZY_MODULES,
            useFactory: lazyArrayToObj
        },
        {
            provide: OneIdService,
            useFactory: OneIdFactory,
            deps: [WindowRef, DebugLogService, ConfigService, LocaleService, LazyLoaderService],
        },
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {
}
